import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

// Lightweight i18n for patient-facing pages. Strings are written in English in the JSX and
// wrapped with t('...'); the English text is looked up in ./es.js (Spanish, formal "usted"),
// ./zh.js (Simplified Chinese, 您) or ./ar.js (Modern Standard Arabic, right-to-left).
// Missing keys fall back to English. Non-English copy is machine-assisted and should get a
// professional medical translation review.
// Language sources, first match wins: ?lang=es|zh|ar|en (or utm_language / hl) → utm ad/campaign hint
// (utmLangHint) → saved choice → browser language (Spanish) → English.
// Detection runs at module load, before landing pages strip their query string.
export const SUPPORTED_LANGS = ['en', 'es', 'zh', 'ar'];
// Dictionaries load on demand (each is 80-120 KB) so English visitors never download them.
// Until a dictionary arrives, strings fall back to English, then re-render in the chosen language.
const DICTS = {};
const DICT_LOADERS = {
  es: () => import('./es'),
  zh: () => import('./zh'),
  ar: () => import('./ar'),
};
const dictPromises = {};
/** Load (once) the dictionary for a language. Resolves to the dictionary or null for English. */
export function loadDict(lang) {
  const loader = DICT_LOADERS[lang];
  if (!loader) return Promise.resolve(null);
  if (DICTS[lang]) return Promise.resolve(DICTS[lang]);
  if (!dictPromises[lang]) {
    dictPromises[lang] = loader().then((m) => {
      DICTS[lang] = m.default || m;
      listeners.forEach((fn) => fn(current, true));
      return DICTS[lang];
    }).catch(() => { delete dictPromises[lang]; return null; });
  }
  return dictPromises[lang];
}
/** True when the language is English or its dictionary has loaded. */
export function isDictReady(lang = current) { return !DICT_LOADERS[lang] || Boolean(DICTS[lang]); }
/** Labels for the language toggle, each written in its own language. */
export const LANG_LABELS = { en: 'EN', es: 'Español', zh: '中文', ar: 'العربية' };
const HTML_LANG = { en: 'en', es: 'es', zh: 'zh-Hans', ar: 'ar' };
const DATE_LOCALE = { en: 'en-US', es: 'es-US', zh: 'zh-CN', ar: 'ar-u-nu-latn' };

/** "zh-Hans", "zh-CN", "ar-SA", "es-419" → supported base code, else null. */
export function normalizeLang(value) {
  const v = String(value || '').toLowerCase().trim();
  if (!v) return null;
  const base = v.startsWith('zh') ? 'zh' : v.slice(0, 2);
  return SUPPORTED_LANGS.includes(base) ? base : null;
}
export const isRtl = (lang) => lang === 'ar';
export const dirFor = (lang) => (isRtl(lang) ? 'rtl' : 'ltr');
/** BCP-47 locale for dates/numbers (Arabic keeps Western digits). */
export const dateLocale = (lang = current) => DATE_LOCALE[lang] || 'en-US';

// <html lang/dir> follow the chosen language ONLY while a translated page is on screen.
// Translated pages register through useTranslatedPage() (LanguageToggle and TranslationNotice do it).
// Every other page (home, /patients, /refer, staff portals) is English-only, so a saved Arabic
// choice must not flip it to right-to-left; those pages always get lang="en" dir="ltr".
let translatedPages = 0;
function applyDocumentLang(lang) {
  const effective = translatedPages > 0 ? lang : 'en';
  try {
    document.documentElement.lang = HTML_LANG[effective] || 'en';
    document.documentElement.dir = dirFor(effective);
  } catch { /* ssr */ }
}
/** Mark the current screen as translated so <html lang/dir> follow the chosen language while mounted. */
export function useTranslatedPage() {
  useLayoutEffect(() => {
    translatedPages += 1;
    applyDocumentLang(current);
    return () => {
      translatedPages = Math.max(0, translatedPages - 1);
      applyDocumentLang(current);
    };
  }, []);
}
const KEY = 'curegate_lang';

// Ad / campaign language hint from utm params (no explicit lang): "_es" / "es_" / "spanish" tags mean
// Spanish; any other tagged ad or campaign visit means English, so a stale saved Spanish choice (or a
// Spanish browser) does not flip an English ad's landing page. Returns null for untagged visits.
export function utmLangHint(q) {
  const tags = [q.get('utm_content'), q.get('utm_campaign')].map((v) => String(v || ''));
  if (tags.some((v) => /(^|[_\-\s|])(es|esp|spanish|espanol|español)($|[_\-\s|])/i.test(v))) return 'es';
  if (q.get('utm_source') || q.get('utm_campaign') || q.get('utm_content')) return 'en';
  return null;
}

/** Language from the URL: ?lang= (or utm_language / hl) first, then utm ad/campaign hints. */
export function langFromSearch(search) {
  try {
    const q = new URLSearchParams(search || '');
    const explicit = normalizeLang(q.get('lang') || q.get('utm_language') || q.get('hl'));
    if (explicit) return explicit;
    return utmLangHint(q);
  } catch { return null; }
}

function readQueryLang() {
  try { return langFromSearch(window.location.search); } catch { return null; }
}
// Last resort before English: the browser's language (Spanish only, the other languages need ?lang=).
function readBrowserLang() {
  try { return normalizeLang((navigator.languages && navigator.languages[0]) || navigator.language) === 'es' ? 'es' : null; } catch { return null; }
}
function readStoredLang() {
  try { return normalizeLang(window.localStorage.getItem(KEY)); } catch { return null; }
}

let current = (typeof window !== 'undefined' && (readQueryLang() || readStoredLang() || readBrowserLang())) || 'en';
if (typeof window !== 'undefined') {
  const q = readQueryLang();
  if (q) { try { window.localStorage.setItem(KEY, q); } catch { /* private mode */ } }
  applyDocumentLang(current);
}
const listeners = new Set();
if (typeof window !== 'undefined' && current !== 'en') loadDict(current);

/** Current language outside React (services, edge-function calls). */
export function currentLang() { return current; }

export function setLang(lang) {
  const next = normalizeLang(lang) || 'en';
  current = next;
  try { window.localStorage.setItem(KEY, next); } catch { /* ignore */ }
  applyDocumentLang(next);
  listeners.forEach((fn) => fn(next));
  loadDict(next);
}

function interpolate(str, vars) {
  if (!vars) return str;
  return String(str).replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : m));
}

/** Translate an English source string to the given language. */
export function translate(en, vars, lang = current) {
  const dict = DICTS[lang];
  if (dict && Object.prototype.hasOwnProperty.call(dict, en)) return interpolate(dict[en], vars);
  return interpolate(en, vars);
}

/** Pick per-language copy: the Spanish string when given, otherwise the dictionary entry for `en`. */
export function tl(lang, en, es) {
  if (lang === 'es' && es) return es;
  return translate(en, null, lang);
}

/**
 * Component copy objects shaped { en: {...}, es: {...} }: returns COPY[lang] when present,
 * otherwise the English copy run through the dictionary (zh/ar).
 */
export function localizeCopy(copy, lang, extra) {
  if (copy?.[lang]) return copy[lang];
  const en = copy?.en || {};
  if (!DICTS[lang]) return en;
  const out = {};
  for (const [k, v] of Object.entries(en)) out[k] = typeof v === 'string' ? translate(v, null, lang) : v;
  return { ...out, ...(extra?.[lang] || {}) };
}

const Ctx = createContext({ lang: 'en', setLang, t: (s, v) => translate(s, v, 'en') });

export function LanguageProvider({ children }) {
  const [lang, setState] = useState(current);
  // Bumps when a dictionary finishes loading so t() re-runs with the translated strings.
  const [dictVersion, setDictVersion] = useState(0);
  useEffect(() => {
    const fn = (l, dictLoaded) => { setState(l); if (dictLoaded) setDictVersion((n) => n + 1); };
    listeners.add(fn);
    if (!isDictReady(current)) loadDict(current);
    return () => listeners.delete(fn);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const t = useCallback((s, v) => translate(s, v, lang), [lang, dictVersion]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang() { return useContext(Ctx); }

/** EN | Español | 中文 | العربية toggle. `tone` = 'dark' (on dark backgrounds) or 'light'. */
export function LanguageToggle({ tone = 'light', className = '', style }) {
  const { lang } = useLang();
  useTranslatedPage();
  const dark = tone === 'dark';
  const base = { fontSize: 13, fontWeight: 600, padding: '4px 10px', borderRadius: 999, border: '1px solid', cursor: 'pointer', background: 'transparent', lineHeight: 1.4 };
  const on = dark ? { background: '#fff', color: '#1d1229', borderColor: '#fff' } : { background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' };
  const off = dark ? { color: '#e9e3f5', borderColor: 'rgba(255,255,255,0.35)' } : { color: '#4b5563', borderColor: '#d1d5db' };
  return (
    <div role="group" aria-label="Language / Idioma / 语言 / اللغة" data-testid="language-toggle" className={className} style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, ...style }}>
      {SUPPORTED_LANGS.map((code) => (
        <button key={code} type="button" lang={HTML_LANG[code]} dir={dirFor(code)} aria-pressed={lang === code} data-testid={`lang-${code}`}
          onClick={() => setLang(code)} style={{ ...base, ...(lang === code ? on : off) }}>{LANG_LABELS[code]}</button>
      ))}
    </div>
  );
}

const NOTICE = {
  es: 'Traducción en revisión. Si algo no está claro, escríbanos a info@curegate.ai.',
  zh: '本页为机器辅助翻译，正在接受专业医学翻译审核。如有不清楚的地方，请发邮件至 info@curegate.ai。',
  ar: 'هذه الترجمة بمساعدة الآلة وقيد المراجعة من مترجم طبي متخصص. إذا كان هناك شيء غير واضح، راسلنا على info@curegate.ai.',
};

/** Small notice shown on translated pages until a professional medical translator has reviewed the copy. */
export function TranslationNotice({ tone = 'light', style }) {
  const { lang } = useLang();
  useTranslatedPage();
  if (!NOTICE[lang]) return null;
  return (
    <p data-testid="translation-notice" style={{ fontSize: 12, margin: '8px 0 0', color: tone === 'dark' ? '#cbc3dc' : '#6b7280', ...style }}>
      {NOTICE[lang]}
    </p>
  );
}
